/**
 * Email-and-password sign-in against Better Auth, on the console's own origin.
 *
 * Extracted from the screen rather than inlined in a submit handler because the
 * interesting part is not the POST — it is the credential hand-over that has to
 * follow it, and a handler wired to a DOM event cannot be proved.
 */
import { clearSession } from "./session";

/**
 * Better Auth's email-and-password endpoint, RELATIVE on purpose.
 *
 * The single most load-bearing string on the sign-in screen. The session cookie
 * Better Auth issues is host-only (`__Secure-better-auth.session_token`, no
 * `Domain=`), so it is stored against whichever host the browser posted to. An
 * absolute URL pointing at the auth Worker or at the Vercel-fronted app would
 * succeed, set the cookie over there, and leave this console with nothing — a
 * login that looks like it worked and then bounces straight back. The console
 * Worker forwards `/api/auth/*` to the auth Worker over a service binding
 * precisely so this can stay relative (`worker/auth-proxy.ts`).
 */
export const SIGN_IN_PATH = "/api/auth/sign-in/email";

export type PasswordLoginOutcome =
  { ok: true } | { ok: false; message: string };

interface Deps {
  fetchFn?: typeof fetch;
  /** Drop whatever bearer this tab still holds. Injected only for tests. */
  forgetStoredBearer?: () => void;
}

/**
 * Sign in, and hand this tab over to the resulting cookie session.
 *
 * On success the session is an HttpOnly cookie the browser now holds and there
 * is nothing to store — but there may be something to DROP. `app/lib/api.ts`
 * attaches any bearer still in sessionStorage, and the Worker lets a presented
 * bearer DECIDE the request with no fallback to the cookie. A stale admin key
 * left over in this tab would therefore either 401 the fresh login or answer
 * `/api/admin/me` as the key's agent, misreporting which door was used. So a
 * successful sign-in forgets it.
 *
 * A REFUSED sign-in forgets nothing: a mistyped password must not log the
 * operator out of a key session that is still perfectly valid.
 */
export async function signInWithPassword(
  credentials: { email: string; password: string },
  deps: Deps = {},
): Promise<PasswordLoginOutcome> {
  const fetchFn = deps.fetchFn ?? fetch;
  const forgetStoredBearer = deps.forgetStoredBearer ?? clearSession;

  const email = credentials.email.trim();
  if (!email || !credentials.password) {
    return { ok: false, message: "Enter your email and password." };
  }

  let response: Response;
  try {
    response = await fetchFn(SIGN_IN_PATH, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      // Same-origin already sends and stores cookies by default; saying so
      // keeps that true rather than leaving it to a default.
      credentials: "same-origin",
      body: JSON.stringify({ email, password: credentials.password }),
    });
  } catch {
    return {
      ok: false,
      message:
        "Could not reach the sign-in service. Check your connection and try again.",
    };
  }

  if (!response.ok) {
    // Better Auth names the refusal itself ("Invalid email or password",
    // "Email not verified"); pass its wording through when there is one, since
    // a generic message would hide which of those an operator is hitting.
    const body = (await response.json().catch(() => null)) as {
      message?: unknown;
    } | null;
    return {
      ok: false,
      message:
        typeof body?.message === "string" && body.message.length > 0
          ? body.message
          : "That email and password did not match.",
    };
  }

  forgetStoredBearer();
  return { ok: true };
}

/**
 * Where to land after a successful sign-in.
 *
 * `gate.tsx` renders the sign-in screen IN PLACE for a signed-out operator, so
 * the browser is already sitting on the deep link they followed — and sign-in
 * used to discard it with `window.location.assign("/")`, dropping every link
 * from Slack, an MCP answer or a bookmark on the Work Queue.
 *
 * The result is fed to a location assignment, so a value that could leave this
 * origin is refused outright rather than cleaned up: a single leading slash
 * followed by a non-slash, and nothing else, is a same-origin path.
 */
export function postSignInDestination(location: {
  pathname: string;
  search: string;
  hash: string;
}): string {
  const { pathname, search, hash } = location;
  if (!/^\/(?!\/)/.test(pathname)) return "/";
  if (pathname === "/") return "/";
  return `${pathname}${search}${hash}`;
}
